import { useState } from "react";
import { toast } from "sonner";
import { Settings, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ProfileSettings() {
  const { user, updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);

  const onOpenChange = (o) => {
    if (o) {
      setName(user?.name || "");
      setEmail(user?.email || "");
      setCurrentPwd("");
      setNewPwd("");
    }
    setOpen(o);
  };

  const save = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Inserisci nome ed email");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name: name.trim(), email: email.trim() });
      updateUser({ name: data.name, email: data.email });
      toast.success("Dati amministratore aggiornati");
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!currentPwd || !newPwd) {
      toast.error("Inserisci la password attuale e quella nuova");
      return;
    }
    if (newPwd.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri");
      return;
    }
    setChangingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPwd, new_password: newPwd });
      toast.success("Password aggiornata");
      setCurrentPwd("");
      setNewPwd("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setChangingPwd(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          data-testid="open-profile-settings"
          className="w-full justify-start gap-3 text-muted-foreground"
        >
          <Settings className="h-4 w-4" /> Dati amministratore
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="profile-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Dati amministratore</DialogTitle>
          <DialogDescription>
            Nome ed email di riferimento. L'email viene usata anche per accedere e per ricevere gli avvisi (es. nuovi docenti da approvare).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="admin-name">Nome</Label>
            <Input id="admin-name" data-testid="admin-name-input" placeholder="Segreteria Didattica"
              value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-email">Email</Label>
            <Input id="admin-email" type="email" data-testid="admin-email-input" placeholder="segreteria@scuola.it"
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button data-testid="save-profile-btn" onClick={save} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva
          </Button>
        </DialogFooter>

        <div className="mt-2 border-t border-border pt-4 space-y-4">
          <div>
            <p className="font-heading font-semibold text-sm">Cambia password</p>
            <p className="text-xs text-muted-foreground">Minimo 8 caratteri.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="current-pwd">Password attuale</Label>
            <Input id="current-pwd" type="password" data-testid="current-password-input" placeholder="••••••••"
              value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-pwd">Nuova password</Label>
            <Input id="new-pwd" type="password" data-testid="new-password-input" placeholder="••••••••"
              value={newPwd} onChange={(e) => setNewPwd(e.target.value)} />
          </div>
          <div className="flex justify-end">
            <Button variant="secondary" data-testid="change-password-btn" onClick={changePassword} disabled={changingPwd}>
              {changingPwd && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Cambia password
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
